import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, MailPlus, Plus, RefreshCw, UsersRound } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type {
  MembershipRole,
  Organization,
  OrganizationInvitation,
  OrganizationMembership,
} from '../../types/domain';

const organizationRoles: Array<{ value: MembershipRole; label: string }> = [
  { value: 'org_admin', label: 'Organisation admin' },
  { value: 'project_manager', label: 'Project manager' },
  { value: 'viewer', label: 'Viewer (read-only)' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'merchandiser', label: 'Merchandiser' },
];
const merchandiserRole: Array<{ value: MembershipRole; label: string }> = [
  { value: 'merchandiser', label: 'Merchandiser' },
];

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const lastActiveFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'Asia/Karachi',
});

export function MembersPage() {
  const { organizationId } = useParams();
  const { accessToken, capabilities, isPlatformAdmin, profile, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<MembershipRole>('org_admin');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MembershipRole>('org_admin');
  const [invitationLink, setInvitationLink] = useState<string | null>(null);
  const organizations = useApiCollection<Organization>(
    'organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
  );
  const organization = organizations.data?.results.find((row) => row.id === organizationId);
  const canManageOrganization = isPlatformAdmin || capabilities.includes('organizations:manage');
  const canManageMerchandisers = isPlatformAdmin || capabilities.includes('merchandising:manage');
  const allowed = Boolean(organizationId) && (canManageOrganization || canManageMerchandisers) && (
    isPlatformAdmin || selectedWorkspace?.organizationId === organizationId
  );
  const members = useApiCollection<OrganizationMembership>(
    'memberships',
    `/memberships/?organization=${organizationId}&page_size=100&ordering=user__full_name`,
    accessToken,
    allowed,
  );
  const invitations = useApiCollection<OrganizationInvitation>(
    'invitations',
    `/invitations/?organization=${organizationId}&page_size=100&ordering=-created_at`,
    accessToken,
    allowed,
  );
  const availableRoles = organization?.type === 'platform'
    ? [{ value: 'platform_admin' as const, label: 'Platform admin' }]
    : canManageOrganization ? organizationRoles : merchandiserRole;
  const effectiveRole = (role: MembershipRole) => (
    organization?.type === 'platform'
      ? 'platform_admin'
      : canManageOrganization ? role : 'merchandiser'
  );

  const addMember = useMutation({
    mutationFn: () => apiRequest<OrganizationMembership>('/memberships/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({
        organization: organizationId,
        user_email: memberEmail,
        role: effectiveRole(memberRole),
        is_active: true,
      }),
    }),
    onSuccess: async () => {
      setMemberEmail('');
      await queryClient.invalidateQueries({ queryKey: ['collection', 'memberships'] });
    },
  });

  const inviteMember = useMutation({
    mutationFn: () => apiRequest<OrganizationInvitation>('/invitations/', {
      ...(accessToken ? { accessToken } : {}),
      method: 'POST',
      body: JSON.stringify({
        organization: organizationId,
        email: inviteEmail,
        role: effectiveRole(inviteRole),
      }),
    }),
    onSuccess: async (invitation) => {
      setInviteEmail('');
      setInvitationLink(invitation.accept_url);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'invitations'] });
    },
  });

  const updateMember = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<Pick<OrganizationMembership, 'role' | 'is_active'>> }) => (
      apiRequest<OrganizationMembership>(`/memberships/${id}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'PATCH',
        body: JSON.stringify(changes),
      })
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collection', 'memberships'] });
    },
  });

  const invitationAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'resend' | 'revoke' }) => (
      apiRequest<OrganizationInvitation>(`/invitations/${id}/${action}/`, {
        ...(accessToken ? { accessToken } : {}),
        method: 'POST',
      })
    ),
    onSuccess: async (invitation) => {
      if (invitation.accept_url) setInvitationLink(invitation.accept_url);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'invitations'] });
    },
  });

  if (!allowed) return <Navigate replace to="/organisations" />;

  const memberRows = members.data?.results ?? [];
  const invitationRows = invitations.data?.results ?? [];

  function submitMember(event: FormEvent) {
    event.preventDefault();
    addMember.mutate();
  }

  function submitInvitation(event: FormEvent) {
    event.preventDefault();
    setInvitationLink(null);
    inviteMember.mutate();
  }

  return (
    <>
      <Link className="back-link" to="/organisations"><ArrowLeft size={15} /> Organisations</Link>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{organization?.type ?? 'Organisation'}</p>
          <h1>{organization?.name ?? 'Members'}</h1>
          <p>Invite new users or manage people who already have Brand Field Crew accounts.</p>
        </div>
        <span className="record-count"><UsersRound size={16} /> {members.data?.count ?? 0}</span>
      </header>

      <div className="management-grid">
        <section className="content-card management-panel">
          <div className="card-heading">
            <div><h2>Invite a new user</h2><p>They will receive a secure account-setup link.</p></div>
          </div>
          <form className="stacked-management-form" onSubmit={submitInvitation}>
            <label>
              Email address
              <input autoComplete="email" placeholder="person@example.com" required type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} />
            </label>
            <label>
              Initial role
              <select value={effectiveRole(inviteRole)} onChange={(event) => setInviteRole(event.target.value as MembershipRole)}>
                {availableRoles.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {inviteMember.isError ? <p className="form-error" role="alert">{getApiErrorMessage(inviteMember.error)}</p> : null}
            <button className="button primary" disabled={inviteMember.isPending} type="submit">{inviteMember.isPending ? <InlineSpinner /> : <MailPlus size={17} />} {inviteMember.isPending ? 'Sending…' : 'Send invitation'}</button>
          </form>
        </section>

        <section className="content-card management-panel">
          <div className="card-heading">
            <div><h2>Add an existing user</h2><p>Use this when the person already has an account.</p></div>
          </div>
          <form className="stacked-management-form" onSubmit={submitMember}>
            <label>
              User email
              <input autoComplete="email" placeholder="person@example.com" required type="email" value={memberEmail} onChange={(event) => setMemberEmail(event.target.value)} />
            </label>
            <label>
              Role
              <select value={effectiveRole(memberRole)} onChange={(event) => setMemberRole(event.target.value as MembershipRole)}>
                {availableRoles.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            {addMember.isError ? <p className="form-error" role="alert">{getApiErrorMessage(addMember.error)}</p> : null}
            <button className="button secondary" disabled={addMember.isPending} type="submit">{addMember.isPending ? <InlineSpinner /> : <Plus size={17} />} {addMember.isPending ? 'Adding…' : 'Add member'}</button>
          </form>
        </section>
      </div>

      {invitationLink ? (
        <section className="development-link">
          <strong>Development invitation link</strong>
          <span>Email is currently delivered to the Django console.</span>
          <a href={invitationLink} rel="noreferrer" target="_blank">Open account setup</a>
        </section>
      ) : null}

      <div className="section-heading"><div><h2>Invitations</h2><p>Pending and historical invitations for this organisation.</p></div><span>{invitations.data?.count ?? 0}</span></div>
      {invitationAction.isError ? <p className="form-error page-error" role="alert">{getApiErrorMessage(invitationAction.error)}</p> : null}
      <DataPanel loading={invitations.isLoading || organizations.isLoading} error={invitations.isError || organizations.isError} empty={invitationRows.length === 0}>
        <section className="content-card data-card records-section">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {invitationRows.map((invitation) => (
                  <tr key={invitation.id}>
                    <td><strong>{invitation.email}</strong><small>Sent {dateFormatter.format(new Date(invitation.last_sent_at))}</small></td>
                    <td><StatusPill value={invitation.role} /></td>
                    <td>{dateFormatter.format(new Date(invitation.expires_at))}</td>
                    <td><StatusPill value={invitation.status} /></td>
                    <td>
                      <div className="table-actions">
                        {invitation.status === 'pending' || invitation.status === 'expired' ? (
                          <button className="text-button" disabled={invitationAction.isPending} onClick={() => invitationAction.mutate({ id: invitation.id, action: 'resend' })} type="button"><RefreshCw size={14} /> Resend</button>
                        ) : null}
                        {invitation.status !== 'accepted' && invitation.status !== 'revoked' ? (
                          <button className="text-button danger-text" disabled={invitationAction.isPending} onClick={() => invitationAction.mutate({ id: invitation.id, action: 'revoke' })} type="button"><Ban size={14} /> Revoke</button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </DataPanel>

      <div className="section-heading"><div><h2>Active members</h2><p>Roles and access currently assigned to users.</p></div><span>{members.data?.count ?? 0}</span></div>
      {updateMember.isError ? <p className="form-error page-error" role="alert">{getApiErrorMessage(updateMember.error)}</p> : null}
      <DataPanel loading={members.isLoading || organizations.isLoading} error={members.isError || organizations.isError} empty={memberRows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Member</th><th>Role</th><th>Last active</th><th>Status</th></tr></thead>
              <tbody>
                {memberRows.map((membership) => {
                  const isOwnMembership = membership.user === profile?.id;
                  const locked = isOwnMembership && !isPlatformAdmin;
                  return (
                    <tr key={membership.id}>
                      <td><strong>{membership.full_name}</strong><small>{membership.email}</small></td>
                      <td>
                        <select
                          aria-label={`Role for ${membership.full_name}`}
                          className="table-select"
                          disabled={locked || updateMember.isPending}
                          value={membership.role}
                          onChange={(event) => updateMember.mutate({ id: membership.id, changes: { role: event.target.value as MembershipRole } })}
                        >
                          {availableRoles.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        {locked ? <small>Your own access is protected</small> : null}
                      </td>
                      <td>
                        {membership.last_seen_at
                          ? lastActiveFormatter.format(new Date(membership.last_seen_at))
                          : 'Never recorded'}
                      </td>
                      <td>
                        <button
                          className="status-button"
                          disabled={locked || updateMember.isPending}
                          onClick={() => updateMember.mutate({ id: membership.id, changes: { is_active: !membership.is_active } })}
                          title={membership.is_active ? 'Deactivate membership' : 'Activate membership'}
                          type="button"
                        >
                          <StatusPill value={membership.is_active ? 'active' : 'inactive'} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </DataPanel>
    </>
  );
}
